/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  Checkbox,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import Loader from "../../components/loader/loader";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import { EFormAction, EOnHandDeliveryStatus, ErrorMessages, FORM_STYLES } from "../../constants";
import { isFormValueChanged } from "../../AppState/isFormValueChanged";
import { compareObjects } from "../../helpers";
import TooltipWithClickAway from "../../components/terminalPicker/TooltipWithClickAway";
import _ from "lodash";
import {
  intiOnHandInvestigationFormValue,
  onHandInvestigationSchema,
} from "../../schema/ohInvestigation";
import NoteBox from "../../components/noteBox/NoteBox";
import { IOhInvestigation } from "../../models/onHandDelivery";
import { OnHandInvestigationAPI } from "../../API/OnHandInvestigationAPI";
import { hasCompleteOnHandPermissionSelector } from "../../AppState/selectors/userPermissionsSelectors";

type Props = {
  osdNumber?: number;
  osdDetails?: any;
  setInvestigationStatus: (status: number) => void;
  osdType?: string;
  isEditMode?: boolean;
};


const OnHandDeliveryInvestigation: React.FC<Props> = ({
  osdNumber = 0,
  osdDetails,
  setInvestigationStatus,
  osdType,
  isEditMode = true,
}) => {
  const dispatch = useDispatch();
  const investigationDetails = useMemo(() => osdDetails, [osdDetails]);
  const hasCompleteOnHandPermission: boolean = useSelector(
    hasCompleteOnHandPermissionSelector
  );
  const [loading, setLoading] = useState(false);
  const [resetTimestamps, setResetTimestamps] = useState<string>(
    Date.now().toString()
  );
  const [status, setStatus] = useState(0);
  const [formFieldsDisabled, setFormFieldsDisabled] = useState(isEditMode);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const [initValues, setInitValues] = React.useState<any>(
    intiOnHandInvestigationFormValue
  );

  const {
    errors,
    values,
    handleBlur,
    handleChange,
    resetForm,
    setFieldValue,
    validateForm,
    handleSubmit,
  } = useFormik({
    validationSchema: onHandInvestigationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    initialValues: initValues,
    onSubmit: (_values) => {},
  });

  const enableEditing = () => {
    setFormFieldsDisabled(false);
  };

  const updateInitValue = (
    key: string,
    value: string | number | null | undefined
  ) => {
    setInitValues((prevState: any) => {
      // Update the top-level property
      return {
        ...prevState,
        [key]: value,
      };
    });
  };

  const excludedKeys = new Set<keyof IOhInvestigation>([
    "hasEmailEntryError",
    "wereAllPartiesContactedWithOhn",
    "wasCommunicationSuccessful",
    "isF18NotesAccurate",
    "wasOhnSentByCreditDept",
    "f18Note",
  ]);
  // Follow-up action
const normalizeYesNoFields = (params: IOhInvestigation) => {
  const yesNoFields: (keyof IOhInvestigation)[] = [
    "hasEmailEntryError",
    "wereAllPartiesContactedWithOhn",
    "wasCommunicationSuccessful",
    "isF18NotesAccurate",
    "wasOhnSentByCreditDept",
    "didTerminalClearException",
    "isF9NotFlashingInAde",
    "hasBolInstructions",
    "followedBolInstructions",
    "doF18NotesMatchActions",
    "hasImageCommunication",
    "wasEveryDeliveryAttemptMade",
    "wasShipperContactedWithin24hrs",
    "isFreightOnHandDueToDamage",
    "ohnVerbiageCorrect",
    "wasContactFrom7823",
    "wasContactFrom8511",
    "googleCheck",
    "pickupScreenCheck",
    "salesRepCheck",
    "isShipperValueInWriting",
    "communicationImagedByTerminal",
    "f18NotesUpdatedByAll",
    "freightHasAppointment",
    "storageChargesApproved",
  ];

  yesNoFields.forEach((field) => {
    const value = params[field];
    params[field] = value === "Y" || value === "N" ? value : undefined;
  });
};

const pruneConditionalFields = (params: IOhInvestigation) => {
  const conditionalDeletes: Partial<Record<keyof IOhInvestigation, (keyof IOhInvestigation)[]>> = {
    isFreightOnHandDueToDamage: [
      "shipmentCallFor",
      "productDescription",
      "packageDamageDescription",
      "actualProductDamageDetails",
      "actualProductNotDamagedDetails",
      "canProductBeRecoupedForRedelivery",
      "isProductRepairable",
      "hasValidDamagePictures",
      "isProductGlass",
    ],
    pickupScreenCheck: ["pickupScreenResponse"],
    wasContactFrom8511: ["stepsTakenFrom8511"],
    salesRepCheck: ["salesRepResponse"],
    googleCheck: ["googleCheckResponse"],
    hasBolInstructions: [
      "followedBolInstructions",
      "doF18NotesMatchActions",
      "hasImageCommunication",
    ],
    wasContactFrom7823: ["stepsTakenFrom7823"],
  };

  Object.entries(conditionalDeletes).forEach(([field, keys]) => {
    if (params[field as keyof IOhInvestigation] !== "Y") {
      keys.forEach((k) => delete params[k]);
    }
  });

  if (params.wasOhnSentByCreditDept === "Y") {
    Object.keys(params).forEach((key) => {
      if (!excludedKeys.has(key as keyof IOhInvestigation)) {
        delete params[key as keyof typeof params];
      }
    });
  }
};

const handleApiSuccess = (
  statusNumber: number,
  params: IOhInvestigation,
  action: EFormAction,
  dispatch: any
) => {
  setStatus(statusNumber);
  setIsCompleting(false);
  setInvestigationStatus(statusNumber);
  setFormFieldsDisabled(true);
  patchFormFieldValues(params);

  const message =
    action === EFormAction.complete
      ? ErrorMessages.ohInvestigationNoteCompleted
      : ErrorMessages.investigationNoteSaved;

  dispatch(
    PopoutNotification(
      NOTIFICATION_TYPE.SUCCESS,
      message,
      Date.now().toString()
    )
  );
};

const handleApiError = (err: unknown, dispatch: any) => {
  const errorMessage =
    typeof err === "string"
      ? err
      : "Exception Investigation failed. Please try again.";

  dispatch(
    PopoutNotification(
      NOTIFICATION_TYPE.ERROR,
      errorMessage,
      Date.now().toString()
    )
  );
  console.error("Exception Investigation error:", err);
};

const saveOrComplete = useCallback(
  async (action = EFormAction.save) => {
    const params: IOhInvestigation = { ...values };

    normalizeYesNoFields(params);
    pruneConditionalFields(params);

    const errors = await validateForm();
    if (
      (action !== EFormAction.save ||
        status === EOnHandDeliveryStatus.srpendingpproval) &&
      Object.keys(errors).length > 0
    ) {
      setIsCompleting(true);
      return;
    }

    setLoading(true);

    try {
      const { statusNumber } = await OnHandInvestigationAPI(
        params,
        osdNumber,
        action
      );
      handleApiSuccess(statusNumber, params, action, dispatch);
    } catch (err) {
      handleApiError(err, dispatch);
    } finally {
      setLoading(false);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [values, dispatch, validateForm, osdNumber, osdType]
);


  const resetToLastSaved = () => {
    resetForm({ values: initValues }); // This resets all values and validations correctly
    setResetTimestamps(Date.now().toString());
  };

  const patchFormFieldValues = (formValues: IOhInvestigation) => {
    Object.entries(intiOnHandInvestigationFormValue).forEach(([key]) => {
      const typedKey = key as keyof IOhInvestigation;
      const value = formValues[typedKey] ?? undefined;

      setFieldValue(typedKey, value, Boolean(value));
      updateInitValue(typedKey, value);
    });
  };

  const renderFormErrorMessage = (
    filedName: string,
    className = "form-error"
  ) => {
    if (errors[filedName] && isCompleting)
      return <span className={className}>{errors[filedName] as string}</span>;
    return null;
  };

  const hasErrors = (fieldKey: string): boolean => {
    return Boolean(_.get(errors, `${fieldKey}`, "") && isCompleting);
  };

  const tolltip = (key: string, text: string) => {
    return <TooltipWithClickAway tooltipText={text} tooltipKey={key} />;
  };
  const renderInputFeild = (
    feildName: string,
    label: string = "",
    placeholder: string = "",
    strike: string = "",
    md: number = 12,
    maxLength: number = 150,
    tooltipText: string = "",
    tooltipKey: string = "",
    disabled: boolean = formFieldsDisabled,
    rows = 1
  ) => { /* NOSONAR */
    return (
      <Grid item md={md} xs={12}>
        <FormControl fullWidth size="small">
          {label && (
            <InputLabel style={FORM_STYLES.LABEL} shrink={true}>
              {label} <sup>{strike}</sup>
              &nbsp;
              {tooltipText && tooltipText !== "" && (
                <span className="cus-tooltip label-tooltip">
                  {tolltip(tooltipKey, tooltipText)}
                </span>
              )}
            </InputLabel>
          )}
          <TextField
            autoComplete="off"
            id={feildName}
            placeholder={placeholder}
            variant="outlined"
            name={feildName}
            rows={rows}
            data-testid={feildName}
            multiline={rows > 1}
            value={values[feildName] ?? ""}
            onChange={handleChange}
            error={hasErrors(feildName)}
            disabled={disabled || formFieldsDisabled}
            inputProps={{ maxLength: maxLength }}
            onBlur={handleBlur}
          />
        </FormControl>
        {renderFormErrorMessage(feildName)}
      </Grid>
    );
  };

  const renderRadioGroup = (
    fieldName: string,
    label: string = "",
    nTooltipKey: string = "",
    nTooltipText: string = "",
    yTooltipKey: string = "",
    yTooltipText: string = ""
  ) => {
    return (
      <Grid item md={12} xs={12} className="pt-0">
        <FormControl className="radio-group question-group">
          <FormLabel>
            {label} <sup>*</sup>
          </FormLabel>
          <RadioGroup row name={fieldName}>
            <FormControlLabel
              value="Y"
              control={<Radio disabled={formFieldsDisabled} />}
              label="Yes"
              name={fieldName}
              checked={values[fieldName] === "Y"}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {values[fieldName] === "Y" && yTooltipText && (
              <div className="tooltip-custom">
                {tolltip(yTooltipKey, yTooltipText)}
              </div>
            )}
            <FormControlLabel
              value="N"
              control={<Radio disabled={formFieldsDisabled} />}
              label="No"
              name={fieldName}
              checked={values[fieldName] === "N"}
              onChange={handleChange}
              onBlur={handleBlur}
              className={
                yTooltipText && values[fieldName] === "Y"
                  ? "margin-left-20"
                  : ""
              }
            />
            {values[fieldName] === "N" && nTooltipText && (
              <div className="tooltip-custom">
                {tolltip(nTooltipKey, nTooltipText)}
              </div>
            )}
          </RadioGroup>
        </FormControl>
        {renderFormErrorMessage(fieldName)}
      </Grid>
    );
  };

  const renderCheckBox = (fieldName: string = "", label: string = "") => {
    return (
      <Grid item md={12} xs={12} className="filter-margin">
        <FormControl className="radio-group question-group">
          <FormLabel>
            <FormControlLabel
              className="checkbox-shoartage"
              control={
                <Checkbox
                  disableRipple
                  checked={values[fieldName] === "Y"}
                  disabled={formFieldsDisabled}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setFieldValue(
                      fieldName,
                      e.target.checked ? "Y" : "N",
                      true
                    );
                  }}
                  name={fieldName}
                  sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                />
              }
              label=""
            />
            {label} <sup>*</sup>
          </FormLabel>
        </FormControl>
        {renderFormErrorMessage(fieldName)}
      </Grid>
    );
  };

  const renderNotes = (message: string) => {
    return (
      <Grid item md={12} xs={12} className="filter-margin">
        <NoteBox data-testid={message} message={`Note: ${message}`} />
      </Grid>
    );
  };

  const isEditable = status !== EOnHandDeliveryStatus.needsCleared;
  const showClearButton =
    status === EOnHandDeliveryStatus.exceptionEntry && !isEditMode;
  const showUpdateButton = status === EOnHandDeliveryStatus.srpendingpproval;
  const showCompleteButton =
    hasCompleteOnHandPermission &&
    status === EOnHandDeliveryStatus.srpendingpproval;

  const actionButtons = () => {
    if (!isEditable) return null;

    return (
      <Grid item md={12} xs={12} className="btn-group-submit">
        {formFieldsDisabled ? (
          <>
            <Button
              variant="contained"
              onClick={enableEditing}
              className="assign ml mr-0"
              data-testid="edit-button"
            >
              Edit
            </Button>
            {showCompleteButton && (
              <Button
                variant="contained"
                onClick={() => saveOrComplete(EFormAction.complete)}
                className="entry ml"
                type="submit"
                data-testid="complete-button"
              >
                Complete
              </Button>
            )}
          </>
        ) : (
          <>
            {showClearButton && (
              <Button
                variant="contained"
                onClick={resetToLastSaved}
                className="clear-btn mr-0 ml"
                disabled={loading}
              >
                Clear
              </Button>
            )}

            {!showClearButton && (
              <Button
                variant="contained"
                onClick={resetToLastSaved}
                className="clear-btn mr-0 ml"
                disabled={loading}
                data-testid="cancel-button"
              >
                Cancel
              </Button>
            )}
            {!showUpdateButton && (
              <>
                <Button
                  variant="contained"
                  onClick={() => saveOrComplete(EFormAction.save)}
                  className="entry ml"
                  type="submit"
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  onClick={() => saveOrComplete(EFormAction.submit)}
                  className="entry ml"
                  type="submit"
                >
                  submit
                </Button>
              </>
            )}
            {showUpdateButton && (
              <Button
                variant="contained"
                onClick={() => saveOrComplete(EFormAction.save)}
                className="assign mr-0 ml"
                type="submit"
              >
                Update
              </Button>
            )}
          </>
        )}
      </Grid>
    );
  };

  const filteredKeys = useMemo(
    () =>
      Object.keys(intiOnHandInvestigationFormValue).filter(
        (key) => !excludedKeys.has(key as keyof IOhInvestigation)
      ),
    []
  );

  useEffect(() => {
    if (!values?.wasOhnSentByCreditDept) return;

    const shouldClear = values.wasOhnSentByCreditDept === "Y";

    filteredKeys.forEach((key) => {
      const typedKey = key as keyof IOhInvestigation;
      const value = shouldClear ? "" : initValues[typedKey] ?? undefined;
      setFieldValue(typedKey, value, true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.wasOhnSentByCreditDept]);

  useEffect(() => {
    dispatch(isFormValueChanged(!compareObjects(initValues, values)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, initValues]);

  useEffect(() => {
    if (!investigationDetails) return;

    patchFormFieldValues(investigationDetails);
    if (investigationDetails?.investigationStatusNumber)
      setStatus(investigationDetails?.investigationStatusNumber);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationDetails]);

  return (
    <>
      <form action="" key={resetTimestamps} onSubmit={handleSubmit}>
        <Grid
          container
          spacing={2}
          className="filter-margin accordion-container"
        >
          <>
            {renderRadioGroup(
              "hasEmailEntryError",
              "Are there any entry errors on an email?"
            )}
            {values?.hasEmailEntryError === "Y" &&
              renderNotes(
                "Resend (option.78/78) the OHN to the correct email address"
              )}
            {renderRadioGroup(
              "wereAllPartiesContactedWithOhn",
              "Were all parties contacted with the OHN notice?"
            )}
            {values?.wereAllPartiesContactedWithOhn === "N" &&
              renderNotes("Use 78/78 to send to all parties")}
            {renderRadioGroup(
              "wasCommunicationSuccessful",
              "Was all communication successful?"
            )}
            {values?.wasCommunicationSuccessful === "N" &&
              renderNotes(
                "Use 78/78 to resend to failed parties with correct contact"
              )}
            {renderRadioGroup(
              "isF18NotesAccurate",
              "Confirm F18 notes are 100% accurate."
            )}
            {values?.isF18NotesAccurate === "N" &&
              renderNotes("Enter correct notes in F18")}
            {renderRadioGroup(
              "wasOhnSentByCreditDept",
              "Was the OHN sent by the credit department?",
              "",
              "",
              "wasOhnSentByCreditDept1",
              "Only report the failures to whoever entered the OH."
            )}

            {values?.wasOhnSentByCreditDept === "N" && (
              <>
                {renderInputFeild(
                  "billDetailsDescription",
                  "Provide details regarding the bill, specifically the item quantities and descriptions? ",
                  "",
                  "*"
                )}
                {renderInputFeild(
                  "onHandDetails",
                  "What is on hand and why (pcs and desc) ",
                  "",
                  "*",
                  12,
                  150,
                  "Confirm it matches the bill",
                  "onHandDetails"
                )}
                {renderRadioGroup(
                  "didTerminalClearException",
                  "Did the terminal clear the delivery exception (PR/RD/RF)?"
                )}

                {values?.didTerminalClearException === "N" &&
                  renderNotes("Clear it in the 78/6")}
                {renderInputFeild(
                  "adeCallReason",
                  "What does the ADE call for?",
                  "",
                  "*",
                  12,
                  150,
                  "Make sure the OH matches and read all the F18 notes",
                  "adeCallReason"
                )}
                {renderRadioGroup(
                  "isF9NotFlashingInAde",
                  "Is the F9 is not flashing in the ADE?"
                )}
                {values?.isF9NotFlashingInAde === "N" &&
                  renderNotes("Email catkinson@saia.com.")}

                {renderRadioGroup(
                  "hasBolInstructions",
                  "Are there any instructions on the BOL or in (78/23)"
                )}

                {values?.hasBolInstructions === "Y" && (
                  <>
                    {renderRadioGroup(
                      "followedBolInstructions",
                      "Did they follow them 100%?"
                    )}
                    {values?.followedBolInstructions === "N" &&
                      renderNotes(
                        "Email the terminal and give detailed instructions"
                      )}
                    {renderRadioGroup(
                      "doF18NotesMatchActions",
                      "Are the F18 notes showing what they did?"
                    )}
                    {values?.doF18NotesMatchActions === "N" &&
                      renderNotes("Have them update F18 notes")}

                    {renderRadioGroup(
                      "hasImageCommunication",
                      "Is there communication in image?",
                      "hasImageCommunication",
                      "We have to show that we have followed the exact instructions provided by the shipper"
                    )}
                    {values?.hasImageCommunication === "N" &&
                      renderNotes("Have them image all the communication")}
                  </>
                )}

                {renderCheckBox(
                  "wasEveryDeliveryAttemptMade",
                  "Confirm every attempt was made to get the freight delivery?"
                )}
                {renderInputFeild("deliveryAttemptSteps")}

                {renderCheckBox(
                  "wasShipperContactedWithin24hrs",
                  "Confirm the shipper was contacted for return authorization and value within 24 hrs of the ADE?"
                )}
                {renderInputFeild("shipperContactSteps")}

                {renderRadioGroup(
                  "isFreightOnHandDueToDamage",
                  "Is the freight On Hand due to damage? "
                )}
                {values.isFreightOnHandDueToDamage === "Y" && (
                  <>
                    {renderInputFeild(
                      "shipmentCallFor",
                      "Exactly what does the shipment call for?",
                      "",
                      "*"
                    )}
                    {renderInputFeild(
                      "productDescription",
                      "What is the product?",
                      "",
                      "*"
                    )}
                    {renderInputFeild(
                      "packageDamageDescription",
                      "What kind of pkg, is there damage to the pkg?",
                      "",
                      "*"
                    )}
                    {renderInputFeild(
                      "actualProductDamageDetails",
                      "Is the actual product damaged, if so, how bad?",
                      "",
                      "*"
                    )}
                    {renderInputFeild(
                      "actualProductNotDamagedDetails",
                      "Is any of the actual product NOT damaged, if so, how much?",
                      "",
                      "*"
                    )}
                    {renderInputFeild(
                      "canProductBeRecoupedForRedelivery",
                      "Can the product be recouped for redelivery?",
                      "",
                      "*"
                    )}
                    {renderInputFeild(
                      "isProductRepairable",
                      "Is this repairable?",
                      "",
                      "*"
                    )}
                    {renderInputFeild(
                      "hasValidDamagePictures",
                      "Are there good pictures of the freight and the damage, in image?",
                      "",
                      "*"
                    )}
                    {renderInputFeild(
                      "isProductGlass",
                      "Is the product glass?",
                      "",
                      "*",
                      12,
                      150,
                      "If glass is broken or shattered email catkinson@ saia.com",
                      "glassIsBroken"
                    )}
                  </>
                )}

                {renderCheckBox(
                  "ohnVerbiageCorrect",
                  "Confirm the verbiage of the OHN is the correct verbiage with specific details of the NON damage?"
                )}
                <Grid item md={12} xs={12} className="filter-margin">
                  <h3 className="accordion-content-header">
                    Confirm the OHN was sent to all parties?
                  </h3>
                </Grid>

                {renderRadioGroup(
                  "wasContactFrom7823",
                  "Did the terminal get their contacts from the 78/23"
                )}
                {values?.wasContactFrom7823 === "Y" &&
                  renderInputFeild("stepsTakenFrom7823")}

                {renderRadioGroup(
                  "wasContactFrom8511",
                  "Did the terminal get their contacts from the 85/11"
                )}

                {values?.wasContactFrom8511 === "Y" &&
                  renderInputFeild("stepsTakenFrom8511")}

                {renderRadioGroup(
                  "googleCheck",
                  "Did we find more contacts from Google?"
                )}

                {values?.googleCheck === "Y" &&
                  renderInputFeild("googleCheckResponse")}

                {renderRadioGroup(
                  "pickupScreenCheck",
                  "Did we find more contacts from the pickup screen?"
                )}
                {values?.pickupScreenCheck === "Y" &&
                  renderInputFeild("pickupScreenResponse")}

                {renderRadioGroup(
                  "salesRepCheck",
                  "Did we get with the sales rep to get more contacts?"
                )}

                {values?.salesRepCheck === "Y" &&
                  renderInputFeild("salesRepResponse")}

                {renderRadioGroup(
                  "isShipperValueInWriting",
                  "Is the value from the shipper, in writing",
                  "isShipperValueInWriting",
                  "If under $800, email catkinson@saia.com for disposition"
                )}
                {values?.isShipperValueInWriting === "N" &&
                  renderNotes("E-mail the shipper for accurate value")}

                {renderCheckBox(
                  "communicationImagedByTerminal",
                  "Confirm all the communication with the shipper is imaged by the terminal?"
                )}

                {renderCheckBox(
                  "f18NotesUpdatedByAll",
                  "Confirm the F18 notes are updated from you and the terminal?"
                )}

                {renderRadioGroup(
                  "freightHasAppointment",
                  "Does the freight have an appointment."
                )}

                {renderRadioGroup(
                  "storageChargesApproved",
                  "Have the storage charges been approved?",
                  "storageChargesApproved",
                  "All the contacts that you have found, including the contacts on the original OHN) with the specific verbiage and give a 72-hour deadline.  Make sure it clearly states the details of the damage product and non-damaged.  Do not use the word damage; describe the actual damage and product."
                )}

                {["Y", "N"].includes(values?.storageChargesApproved) &&
                  renderNotes(
                    values?.storageChargesApproved === "Y"
                      ? "Image e-mail of approval"
                      : "Send the final email to all parties"
                  )}
              </>
            )}

            {renderInputFeild(
              "f18Note",
              "F18 Note",
              "Enter F18 Note",
              "*",
              12,
              150,
              "",
              "",
              formFieldsDisabled,
              2
            )}
            {/* Action Buttons */}
            {actionButtons()}
          </>
        </Grid>
      </form>
      <Loader pageLoader={loading} />
    </>
  );
};

export default React.memo(OnHandDeliveryInvestigation);
